import * as React from "react";

import { ChapterMenuIcon } from "../icons/general/icons";
import { MeditationIcon } from "../icons/sharing/icons";
import { cn } from "../lib/cn";
import { PlayPauseButton } from "./play-pause-button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { SeekBackwardButton } from "./seek-backward-button";
import { SeekBar } from "./seek-bar";
import { SeekForwardButton } from "./seek-forward-button";
import {
  type SeekBarSegment,
  SegmentedSeekBar,
  type SegmentedSeekBarTooltipContext,
} from "./segmented-seek-bar";

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export type PlayerControlsProps = {
  position: number;
  duration: number;
  playing: boolean;
  disabled?: boolean;
  onSeek: (position: number) => void;
  onSeekEnd: (position: number) => void;
  onSeekStart?: () => void;
  onSeekForward: () => void;
  onSeekBackward: () => void;
  onPlayPause: () => void;
  sourcePage?: string;
  color?: "black" | "white";
  className?: string;
  variant?: "fixed" | "inline";
  leftTimeLabel?: string | null;
  rightTimeLabel?: string | null;
  seekBarMode?: "plain" | "segmented";
  segments?: Array<SeekBarSegment>;
  ariaValueText?: string;
  onStepChapter?: (direction: "previous" | "next") => void;
  renderSeekTooltip?: (ctx: SegmentedSeekBarTooltipContext) => React.ReactNode;
  showSeekTooltip?: boolean;
  onOpenChapters?: () => void;
  chapterMenuContent?:
    | React.ReactNode
    | ((args: { closeMenu: () => void }) => React.ReactNode);
  onOpenMeditation?: () => void;
};

type SideActionButtonProps = {
  ariaLabel: string;
  color: "black" | "white";
  disabled: boolean;
  onClick?: () => void;
  testId: string;
  children: React.ReactNode;
};

const SideActionButton = React.forwardRef<
  HTMLButtonElement,
  SideActionButtonProps
>(({ ariaLabel, color, disabled, onClick, testId, children }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={ariaLabel}
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "flex size-[36px] shrink-0 items-center justify-center rounded-[8px] backdrop-blur-2xl",
      color === "white"
        ? "bg-[linear-gradient(0deg,rgba(16,16,16,0.15),rgba(16,16,16,0.15)),linear-gradient(0deg,rgba(255,255,255,0.05),rgba(255,255,255,0.05))] text-white"
        : "bg-black/5 text-black",
      disabled && "cursor-not-allowed opacity-50",
    )}
    aria-disabled={disabled}
  >
    {children}
  </button>
));
SideActionButton.displayName = "SideActionButton";

/**
 * Reserves the footprint of a hidden side action so the centered transport
 * controls keep their position when chapters/meditation are not configured.
 */
function SideActionPlaceholder() {
  return <span aria-hidden="true" className="size-[36px] shrink-0" />;
}

export const PlayerControls = React.forwardRef<
  HTMLDivElement | HTMLInputElement,
  PlayerControlsProps
>(
  (
    {
      position,
      duration,
      playing,
      disabled = false,
      onSeek,
      onSeekEnd,
      onSeekStart,
      onSeekForward,
      onSeekBackward,
      onPlayPause,
      sourcePage: _sourcePage,
      color = "white",
      className,
      leftTimeLabel,
      rightTimeLabel,
      seekBarMode = "plain",
      segments = [],
      ariaValueText,
      onStepChapter,
      renderSeekTooltip,
      showSeekTooltip = true,
      onOpenChapters,
      chapterMenuContent,
      onOpenMeditation,
    },
    ref,
  ) => {
    const [isChapterMenuOpen, setIsChapterMenuOpen] = React.useState(false);
    const currentPositionLabel =
      leftTimeLabel ?? formatTime(Math.max(position, 0));
    const [currentTimeLabel, ...currentChapterParts] =
      currentPositionLabel.split(" • ");
    const currentChapterLabel = currentChapterParts.join(" • ");
    const currentTimeDigitCount = currentTimeLabel.replace(
      /[^0-9]/g,
      "",
    ).length;
    const timeSlotMinWidth = `${currentTimeDigitCount}ch`;
    const sideIconColor = color === "white" ? "white" : "currentColor";
    const hasChapterMenu = Boolean(chapterMenuContent);
    const hasChapterAction = hasChapterMenu || Boolean(onOpenChapters);
    const hasMeditationAction = Boolean(onOpenMeditation);

    const handleChapterMenuOpenChange = (nextOpen: boolean) => {
      setIsChapterMenuOpen(nextOpen);
      if (nextOpen) {
        onOpenChapters?.();
      }
    };

    const resolvedChapterMenuContent =
      typeof chapterMenuContent === "function"
        ? chapterMenuContent({
            closeMenu: () => setIsChapterMenuOpen(false),
          })
        : chapterMenuContent;
    const timestampColor =
      color === "white" ? "text-twi-gray-300" : "text-black";
    const shouldUseSegmentedSeekBar =
      seekBarMode === "segmented" && segments.length > 0;
    const remainingTimeLabel =
      rightTimeLabel ?? `-${formatTime(Math.max(duration - position, 0))}`;

    return (
      <div className={cn("flex flex-col gap-1 pt-4 pb-2", className)}>
        <div className="flex flex-col gap-1">
          {shouldUseSegmentedSeekBar ? (
            <SegmentedSeekBar
              ref={ref as React.ForwardedRef<HTMLDivElement>}
              value={position}
              max={duration}
              segments={segments}
              color={color}
              onSeek={onSeek}
              onSeekEnd={onSeekEnd}
              onSeekStart={onSeekStart}
              onStepChapter={onStepChapter}
              disabled={disabled}
              ariaValueText={ariaValueText}
              renderTooltip={renderSeekTooltip}
              showTooltip={showSeekTooltip}
              data-testid="audio-player-seek"
            />
          ) : (
            <SeekBar
              ref={ref as React.ForwardedRef<HTMLInputElement>}
              value={position}
              max={duration}
              onSeek={onSeek}
              onSeekEnd={onSeekEnd}
              onSeekStart={onSeekStart}
              disabled={disabled}
              color={color}
              trackSize="thin"
            />
          )}
          <div
            className={cn(
              "flex items-center justify-between text-[12px] leading-normal font-medium tabular-nums",
              timestampColor,
            )}
          >
            <span className="flex select-none items-center gap-1">
              <span
                className="inline-block text-left"
                style={{ minWidth: timeSlotMinWidth }}
              >
                {currentTimeLabel}
              </span>
              {currentChapterLabel ? (
                <>
                  <span aria-hidden="true" className="mx-1">
                    •
                  </span>
                  <span>{currentChapterLabel}</span>
                </>
              ) : null}
            </span>
            <span className="select-none">{remainingTimeLabel}</span>
          </div>
        </div>
        <div className="flex h-16 w-full items-center justify-between">
          {!hasChapterAction ? (
            <SideActionPlaceholder />
          ) : hasChapterMenu ? (
            <Popover
              open={isChapterMenuOpen}
              onOpenChange={handleChapterMenuOpenChange}
            >
              <PopoverTrigger asChild>
                <SideActionButton
                  ariaLabel="Open chapter menu"
                  color={color}
                  disabled={disabled}
                  testId="audio-chapter-menu-button"
                >
                  <ChapterMenuIcon className="size-4" />
                </SideActionButton>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                sideOffset={12}
                className="w-auto border-none bg-transparent p-0 shadow-none"
              >
                {resolvedChapterMenuContent}
              </PopoverContent>
            </Popover>
          ) : (
            <SideActionButton
              ariaLabel="Open chapter menu"
              color={color}
              disabled={disabled}
              onClick={onOpenChapters}
              testId="audio-chapter-menu-button"
            >
              <ChapterMenuIcon className="size-4" />
            </SideActionButton>
          )}
          <div className="flex flex-1 items-center justify-center gap-10">
            <SeekBackwardButton
              onSeekBackward={onSeekBackward}
              size="player"
              color={color}
              disabled={disabled}
            />
            <PlayPauseButton
              playing={playing}
              size="player"
              color={color}
              disabled={disabled}
              onClick={onPlayPause}
              dataContext="drawer"
            />
            <SeekForwardButton
              onSeekForward={onSeekForward}
              size="player"
              color={color}
              disabled={disabled}
            />
          </div>
          {hasMeditationAction ? (
            <SideActionButton
              ariaLabel="Open meditation"
              color={color}
              disabled={disabled}
              onClick={onOpenMeditation}
              testId="audio-meditation-button"
            >
              <MeditationIcon className="size-4" color={sideIconColor} />
            </SideActionButton>
          ) : (
            <SideActionPlaceholder />
          )}
        </div>
      </div>
    );
  },
);
PlayerControls.displayName = "PlayerControls";
